import React, { useEffect, useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { fetchDataflows } from "../api/client";
import { DataflowRecord, DataflowDatasource } from "../types";
import DataTable from "../components/DataTable";
import ExportButton from "../components/ExportButton";
import DetailModal from "../components/DetailModal";
import ShowPersonalToggle from "../components/ShowPersonalToggle";
import useWorkspaceList from "../hooks/useWorkspaceList";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

export default function DataflowsPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [dataflows, setDataflows] = useState<DataflowRecord[]>([]);
  const { workspaces, loading: wsLoading, showPersonal, setShowPersonal } = useWorkspaceList();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [modalDataflow, setModalDataflow] = useState<DataflowRecord | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (workspaceFilter) params.workspace_id = workspaceFilter;

    fetchDataflows(params)
      .then((data) => setDataflows(data))
      .catch((err) => setError(err.message ?? "Failed to load dataflows."))
      .finally(() => setLoading(false));
  }, [workspaceFilter]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceFilter]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<DataflowRecord, any>[] = [
    { accessorKey: "dataflowName", header: "Dataflow Name", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workspaceName", header: "Workspace", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "configuredBy", header: "Configured By", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "modifiedBy", header: "Modified By", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "modifiedDateTime",
      header: "Last Modified",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "datasources",
      header: "Data Sources",
      accessorFn: (row) => row.datasources?.length ?? 0,
      cell: ({ row, getValue }) => {
        const count = getValue() as number;
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
              {count}
            </span>
            {count > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setModalDataflow(row.original); }}
                className="text-xs text-blue-600 hover:underline"
              >
                Details
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dataflows</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Power BI dataflow audit — configurations and data sources.
          </p>
        </div>
        <ExportButton
          endpoint="/api/export/dataflows"
          params={{ workspace_id: workspaceFilter || undefined }}
          filename="dataflows.csv"
        />
      </div>

      {/* Workspace filter */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Workspace</label>
          <select
            value={workspaceFilter}
            onChange={(e) => setWorkspaceFilter(e.target.value)}
            disabled={wsLoading}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-56 disabled:text-gray-400"
          >
            <option value="">{wsLoading ? "Loading workspaces…" : "All workspaces"}</option>
            {workspaces.map((ws) => (
              <option key={ws.workspaceId} value={ws.workspaceId}>{ws.name}</option>
            ))}
          </select>
        </div>
        <ShowPersonalToggle show={showPersonal} onChange={setShowPersonal} />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading dataflows…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && dataflows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
          <p className="text-lg font-semibold">No dataflows found</p>
        </div>
      )}

      {!loading && !error && dataflows.length > 0 && (
        <DataTable data={dataflows} columns={columns} initialPageSize={25} />
      )}

      {modalDataflow && (
        <DetailModal
          title={`Data Sources — ${modalDataflow.dataflowName}`}
          subtitle={modalDataflow.workspaceName}
          onClose={() => setModalDataflow(null)}
        >
          {modalDataflow.datasources.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4">Connection Details</th>
                  <th className="pb-2">Gateway ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {modalDataflow.datasources.map((ds: DataflowDatasource, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-1.5 pr-4 text-gray-700 font-medium">{ds.datasourceType}</td>
                    <td className="py-1.5 pr-4 text-gray-500 text-xs font-mono break-all" title={ds.connectionDetails ?? ""}>
                      {ds.connectionDetails || "—"}
                    </td>
                    <td className="py-1.5 text-gray-400 text-xs">{ds.gatewayId || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-sm">No data sources recorded.</p>
          )}
        </DetailModal>
      )}
    </div>
  );
}
